/**
 * @fileoverview Module icon helper used throughout the core UI to normalize icon identifiers.
 * 
 * @purpose Map Remixicon-style identifiers (e.g. `ri-chat-1-line`) to Lucide icons so every module card shares a consistent glyph set.
 * @usage Imported by `NormalHomepage` and other shared layouts that render module tiles.
 * @ownership Shared UI components (core)
 * @dependencies
 * - lucide-react (individual icon glyphs)
 * - react (CSSProperties type)
 * - @/core/lib/utils (class name helper)
 * @restrictions
 * - DO NOT expose icons that are not mapped (use fallback puzzle).
 * - DO NOT change class names that depend on inline `cn` output.
 */
import { cn } from '@korvo/core/lib/utils';
import {
  Bell,
  BookOpen,
  Cpu,
  FileText,
  Headphones,
  HelpCircle,
  Home,
  LayoutGrid,
  LogOut,
  type LucideIcon,
  Menu,
  MessageSquare,
  Moon,
  Pencil,
  Puzzle,
  Settings,
  Shield,
  Sun,
  Target,
  User,
  X,
} from 'lucide-react';
import type { CSSProperties } from 'react';

type ModuleIconProps = {
  /** Remixicon-class-like identifier (e.g. `ri-chat-1-line`). Unknown values fall back to a puzzle icon. */
  icon?: string | null;
  className?: string;
  style?: CSSProperties;
};

const iconMap: Record<string, LucideIcon> = {
  'ri-admin-line': Shield,
  'ri-apps-2-line': LayoutGrid,
  'ri-book-2-line': BookOpen,
  'ri-chat-1-line': MessageSquare,
  'ri-close-line': X,
  'ri-cpu-line': Cpu,
  'ri-customer-service-2-line': Headphones,
  'ri-edit-2-line': Pencil,
  'ri-file-text-line': FileText,
  'ri-home-4-line': Home,
  'ri-logout-box-line': LogOut,
  'ri-menu-line': Menu,
  'ri-moon-line': Moon,
  'ri-notification-3-line': Bell,
  'ri-notification-line': Bell,
  'ri-puzzle-line': Puzzle,
  'ri-question-line': HelpCircle,
  'ri-settings-3-line': Settings,
  'ri-sun-line': Sun,
  'ri-target-line': Target,
  'ri-user-line': User,
};

export function ModuleIcon({ icon, className, style }: ModuleIconProps) {
  const Icon = (icon ? iconMap[icon] : undefined) ?? Puzzle;
  return <Icon className={cn('inline-block', className)} style={style} aria-hidden="true" />;
}

